import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { formatDistanceToNow } from 'date-fns';
import { buildEmailLink, buildWhatsAppLink } from './AdminSettings';

export default function PasswordResets() {
  const [requests, setRequests] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [toast, setToast] = useState('');
  const [resetModal, setResetModal] = useState(null); // { request, customer }
  const [newPassword, setNewPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [tableExists, setTableExists] = useState(true);
  const [resetDone, setResetDone] = useState(false);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const load = useCallback(async () => {
    setLoading(true);

    // Load customers for reference
    const { data: custData } = await supabase.from('customers').select('*');
    setCustomers(custData || []);

    // Try to load password_resets
    const { data, error } = await supabase.from('password_resets')
      .select('*').order('created_at', { ascending: false });

    if (error && error.message?.includes('password_resets')) {
      setTableExists(false);
    } else {
      setRequests(data || []);
      setTableExists(true);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = requests.filter(r => filter === 'all' || r.status === filter);

  const pendingCount = requests.filter(r => r.status === 'pending').length;

  const handleReset = async () => {
    if (!newPassword || newPassword.length < 6) {
      showToast('Password must be at least 6 characters');
      return;
    }
    setSaving(true);
    try {
      const customer = resetModal.customer;
      if (!customer?.auth_id) throw new Error('Customer has no auth_id');

      // Reset password via admin API
      const { error: authErr } = await supabase.auth.admin.updateUserById(customer.auth_id, {
        password: newPassword,
      });
      if (authErr) throw authErr;

      // Mark request as completed
      await supabase.from('password_resets').update({
        status: 'completed',
        resolved_at: new Date().toISOString(),
        admin_notes: 'Password reset by admin',
      }).eq('id', resetModal.request.id);

      showToast(`Password reset for ${customer.name}!`);
      setResetDone(true);
      load();
    } catch (e) {
      showToast('Error: ' + e.message);
    }
    setSaving(false);
  };

  const rejectRequest = async (id) => {
    await supabase.from('password_resets').update({
      status: 'rejected',
      resolved_at: new Date().toISOString(),
      admin_notes: 'Rejected by admin',
    }).eq('id', id);
    showToast('Request rejected');
    load();
  };

  const generatePassword = () => {
    const chars = 'abcdefghijkmnpqrstuvwxyz23456789';
    let pw = '';
    for (let i = 0; i < 8; i++) pw += chars[Math.floor(Math.random() * chars.length)];
    setNewPassword(pw);
  };

  if (loading) return <div style={{ padding: 40, color: '#64748b' }}>Loading...</div>;

  // Table doesn't exist yet — show setup instructions
  if (!tableExists) {
    return (
      <div>
        <h1 className="page-title">Password Resets</h1>
        <div className="card" style={{ textAlign: 'center', padding: '40px 24px' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>{'\uD83D\uDD10'}</div>
          <h2 style={{ fontSize: 18, fontWeight: 800, color: '#0f172a', marginBottom: 8 }}>
            Setup Required
          </h2>
          <p style={{ fontSize: 14, color: '#64748b', maxWidth: 500, margin: '0 auto 24px', lineHeight: 1.6 }}>
            The <code style={{ background: '#f1f5f9', padding: '2px 6px', borderRadius: 4 }}>password_resets</code> table
            needs to be created in your Supabase database. Run this SQL in your
            Supabase SQL Editor:
          </p>
          <div style={{
            background: '#0f172a', color: '#e2e8f0', padding: '16px 20px',
            borderRadius: 10, textAlign: 'left', fontSize: 12,
            fontFamily: 'monospace', lineHeight: 1.7, overflowX: 'auto',
            maxWidth: 600, margin: '0 auto',
          }}>
            <div style={{ color: '#64748b' }}>-- Run this in Supabase SQL Editor</div>
            CREATE TABLE password_resets (<br/>
            &nbsp;&nbsp;id UUID PRIMARY KEY DEFAULT gen_random_uuid(),<br/>
            &nbsp;&nbsp;email TEXT NOT NULL,<br/>
            &nbsp;&nbsp;customer_name TEXT,<br/>
            &nbsp;&nbsp;customer_id UUID,<br/>
            &nbsp;&nbsp;status TEXT DEFAULT 'pending',<br/>
            &nbsp;&nbsp;admin_notes TEXT,<br/>
            &nbsp;&nbsp;created_at TIMESTAMPTZ DEFAULT now(),<br/>
            &nbsp;&nbsp;resolved_at TIMESTAMPTZ<br/>
            );<br/><br/>
            ALTER TABLE password_resets ENABLE ROW LEVEL SECURITY;<br/><br/>
            CREATE POLICY "allow_insert" ON password_resets<br/>
            &nbsp;&nbsp;FOR INSERT WITH CHECK (true);<br/><br/>
            CREATE POLICY "allow_all_service" ON password_resets<br/>
            &nbsp;&nbsp;FOR ALL USING (true) WITH CHECK (true);
          </div>
          <p style={{ fontSize: 12, color: '#94a3b8', marginTop: 16 }}>
            After running this, refresh this page.
          </p>
          <button className="btn" onClick={load} style={{ marginTop: 12 }}>
            Refresh
          </button>

          {/* Meanwhile, show manual reset option */}
          <div style={{ marginTop: 32, paddingTop: 24, borderTop: '1px solid #e2e8f0' }}>
            <h3 style={{ fontSize: 14, fontWeight: 700, color: '#374151', marginBottom: 8 }}>
              Meanwhile, reset passwords from Customers page
            </h3>
            <p style={{ fontSize: 13, color: '#64748b' }}>
              Go to Customers {'\u2192'} click the {'\uD83D\uDD11'} key icon next to any customer to reset their password.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
        <h1 className="page-title">
          Password Resets
          {pendingCount > 0 && (
            <span className="badge badge-red" style={{ marginLeft: 8, fontSize: 12 }}>
              {pendingCount} pending
            </span>
          )}
        </h1>
      </div>

      {/* Filter */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 18 }}>
        {['pending', 'completed', 'rejected', 'all'].map(f => (
          <button key={f} className={`btn-sm ${filter === f ? 'active' : ''}`}
            onClick={() => setFilter(f)}
            style={{
              background: filter === f ? '#0f172a' : undefined,
              color: filter === f ? '#fff' : undefined,
            }}>
            {f.charAt(0).toUpperCase() + f.slice(1)}
            {f === 'pending' && pendingCount > 0 && (
              <span style={{
                background: '#ef4444', color: '#fff', padding: '1px 6px',
                borderRadius: 99, fontSize: 10, marginLeft: 6,
              }}>
                {pendingCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Requests */}
      {filtered.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '40px 20px' }}>
          <div style={{ fontSize: 40, marginBottom: 8 }}>
            {filter === 'pending' ? '\u2705' : '\uD83D\uDCE8'}
          </div>
          <div style={{ fontWeight: 600, color: '#374151', marginBottom: 4 }}>
            {filter === 'pending' ? 'No pending requests' : `No ${filter} requests`}
          </div>
          <div style={{ fontSize: 13, color: '#94a3b8' }}>
            {filter === 'pending'
              ? 'All password reset requests have been handled!'
              : 'Nothing to show for this filter.'}
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.map(req => {
            const cust = customers.find(c => c.id === req.customer_id || c.email === req.email);
            const isPending = req.status === 'pending';

            return (
              <div key={req.id} className="card" style={{
                marginBottom: 0,
                borderLeft: `4px solid ${isPending ? '#f59e0b' : req.status === 'completed' ? '#22c55e' : '#94a3b8'}`,
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                  <div style={{
                    width: 42, height: 42, borderRadius: 10,
                    background: isPending ? '#fef3c7' : req.status === 'completed' ? '#dcfce7' : '#f1f5f9',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 18, flexShrink: 0,
                  }}>
                    {isPending ? '\uD83D\uDD10' : req.status === 'completed' ? '\u2705' : '\u274C'}
                  </div>

                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <span style={{ fontWeight: 700, fontSize: 14, color: '#0f172a' }}>
                        {req.customer_name || 'Unknown'}
                      </span>
                      <span className={`badge ${isPending ? 'badge-orange' : req.status === 'completed' ? 'badge-green' : 'badge-gray'}`}>
                        {req.status}
                      </span>
                    </div>
                    <div style={{ fontSize: 13, color: '#64748b', marginBottom: 4 }}>{req.email}</div>
                    <div style={{ fontSize: 11, color: '#94a3b8' }}>
                      Requested {formatDistanceToNow(new Date(req.created_at), { addSuffix: true })}
                      {req.resolved_at && (
                        <span> {'\u00B7'} Resolved {formatDistanceToNow(new Date(req.resolved_at), { addSuffix: true })}</span>
                      )}
                    </div>
                    {req.admin_notes && (
                      <div style={{ fontSize: 12, color: '#64748b', marginTop: 4, fontStyle: 'italic' }}>
                        {req.admin_notes}
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  {isPending && (
                    <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                      <button className="btn" style={{
                        marginTop: 0, padding: '8px 16px', fontSize: 12,
                        background: 'linear-gradient(135deg, #3b82f6, #6366f1)',
                      }}
                        onClick={() => { setResetModal({ request: req, customer: cust }); generatePassword(); }}>
                        Reset Password
                      </button>
                      <button className="btn-sm" style={{ background: '#fee2e2', color: '#dc2626' }}
                        onClick={() => rejectRequest(req.id)}>
                        Reject
                      </button>
                    </div>
                  )}

                  {/* WhatsApp link for customer */}
                  {cust?.phone && (
                    <a href={`https://wa.me/${cust.phone.replace(/\D/g, '')}`}
                      target="_blank" rel="noreferrer"
                      style={{
                        background: '#dcfce7', color: '#16a34a',
                        padding: '8px 12px', borderRadius: 8,
                        fontSize: 12, fontWeight: 600, textDecoration: 'none',
                        flexShrink: 0,
                      }}>
                      WhatsApp
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Reset password modal */}
      {resetModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ width: '95%', maxWidth: 420 }}>
            <div className="modal-title">{resetDone ? 'Password Reset Successful!' : 'Reset Password'}</div>

            <div style={{
              background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10,
              padding: '12px 16px', marginBottom: 16,
            }}>
              <div style={{ fontWeight: 700, fontSize: 14 }}>{resetModal.request.customer_name}</div>
              <div style={{ fontSize: 12, color: '#64748b' }}>{resetModal.request.email}</div>
              {resetModal.customer?.phone && (
                <div style={{ fontSize: 12, color: '#22c55e', marginTop: 2 }}>
                  {resetModal.customer.phone}
                </div>
              )}
            </div>

            {!resetDone ? (
              <>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>
                    New Password
                  </label>
                  <input type="text" value={newPassword} onChange={e => setNewPassword(e.target.value)}
                    placeholder="Min 6 characters" style={{ fontFamily: 'monospace', fontSize: 16, letterSpacing: '0.1em' }} />
                </div>

                <button className="btn-sm" onClick={generatePassword}
                  style={{ width: '100%', marginBottom: 16, background: '#f0f9ff', color: '#0ea5e9' }}>
                  Generate Random Password
                </button>

                {newPassword && newPassword.length >= 6 && (
                  <div style={{
                    background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8,
                    padding: '12px 16px', marginBottom: 16, textAlign: 'center',
                  }}>
                    <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>New password:</div>
                    <div style={{ fontSize: 22, fontWeight: 800, fontFamily: 'monospace', color: '#166534', letterSpacing: '0.12em' }}>
                      {newPassword}
                    </div>
                  </div>
                )}

                <div style={{ display: 'flex', gap: 10 }}>
                  <button className="btn-sm" style={{ flex: 1 }} onClick={() => { setResetModal(null); setNewPassword(''); setResetDone(false); }}>
                    Cancel
                  </button>
                  <button className="btn" style={{ flex: 1, marginTop: 0 }} onClick={handleReset} disabled={saving}>
                    {saving ? 'Resetting...' : 'Reset & Mark Done'}
                  </button>
                </div>
              </>
            ) : (
              /* Success state — Send to customer */
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 48, marginBottom: 8 }}>✅</div>

                <div style={{
                  background: '#f0fdf4', border: '2px solid #bbf7d0', borderRadius: 12,
                  padding: '16px 20px', marginBottom: 20,
                }}>
                  <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>New password:</div>
                  <div style={{
                    fontSize: 28, fontWeight: 800, fontFamily: 'monospace',
                    color: '#166534', letterSpacing: '0.15em',
                  }}>
                    {newPassword}
                  </div>
                </div>

                <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 10 }}>
                  Send new password to customer:
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
                  {/* Send via Email */}
                  <a href={buildEmailLink(resetModal.request.email, resetModal.request.customer_name, newPassword)}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                      padding: '14px 20px', borderRadius: 10, textDecoration: 'none',
                      background: 'linear-gradient(135deg, #3b82f6, #6366f1)', color: '#fff',
                      fontWeight: 700, fontSize: 14, cursor: 'pointer',
                    }}>
                    ✉️ Send via Email
                    <span style={{ fontSize: 11, fontWeight: 400, opacity: 0.8 }}>
                      (opens your email app)
                    </span>
                  </a>

                  {/* Send via WhatsApp */}
                  {resetModal.customer?.phone && (
                    <a href={buildWhatsAppLink(resetModal.customer.phone, resetModal.request.customer_name, newPassword)}
                      target="_blank" rel="noreferrer"
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                        padding: '14px 20px', borderRadius: 10, textDecoration: 'none',
                        background: '#25D366', color: '#fff',
                        fontWeight: 700, fontSize: 14, cursor: 'pointer',
                      }}>
                      💬 Send via WhatsApp
                    </a>
                  )}

                  {/* Copy */}
                  <button className="btn-sm" onClick={() => {
                    navigator.clipboard.writeText(newPassword);
                    showToast('Password copied!');
                  }} style={{ width: '100%', background: '#f1f5f9', color: '#475569', padding: '10px' }}>
                    📋 Copy Password
                  </button>
                </div>

                <button className="btn-sm" onClick={() => { setResetModal(null); setNewPassword(''); setResetDone(false); }}
                  style={{ width: '100%', marginTop: 4 }}>
                  Done
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}
